import React, { useEffect, useMemo, useState } from "react";
import {
  Modal,
  Drawer,
  Flex,
  Typography,
  Button,
  Spin,
  message,
  Empty,
  Radio,
} from "antd";
import {
  ArrowLeftOutlined,
  EnvironmentOutlined,
  TruckOutlined,
  FileTextOutlined,
  EditOutlined,
} from "@ant-design/icons";
import { axiosInstance } from "@/utilities";
import { API_URL_QUOTES, API_URL_USER_ADDRESSES_V1 } from "@/services/XCONS_ENV";
import { DeliveryAgenda } from "@/pages/contacts/Address/deliveryAgenda";
import { QuoteBillingAgendaForm } from "./QuoteBillingAgendaForm";
import {
  fromV1,
  toV1Create,
  toV1Update,
} from "@/pages/contacts/Address/agenda.adapter";

const { Text, Title } = Typography;

type AddressType = "shipping" | "billing";

interface QuoteAddressBookPopupProps {
  open: boolean;
  onClose: () => void;
  quoteId: number;
  userId: number;
  type: AddressType;
  quoteLabel?: string | number;
  /** Quote's storeCode; if present the agenda fetch is scoped to this store. */
  storeCode?: string | null;
  onSaved: () => void;
}

const formatAddressLine = (a: any) => {
  const parts = [a?.street, a?.city, a?.region, a?.postcode].filter(Boolean);
  return parts.join(", ");
};

/**
 * Varios flujos guardan un guion como relleno en los campos que no aplican
 * (`company`, `street1`, ciudad/provincia/CP del Consumidor Final), así que un
 * "-" es un campo VACÍO, no un dato. Medido en la agenda: 12.417 filas de
 * facturación tienen `company = '-'` y un nombre real al lado.
 */
const cleanField = (v: any) => {
  const s = String(v ?? "").trim();
  return /^-+$/.test(s) ? "" : s;
};

/**
 * Título de la tarjeta de la agenda.
 *
 * En facturación manda la **razón social** (`company`), no el nombre del
 * cliente: la fila de billing arrastra el `firstname`/`lastname` del titular de
 * la cuenta, así que priorizar el nombre tapaba la razón social cargada. Para
 * Consumidor Final el propio form guarda el nombre y apellido en `company`, así
 * que la preferencia sigue mostrando lo correcto; y si `company` viene vacío o
 * en guion, cae al nombre igual que antes.
 *
 * En envío es al revés: la tarjeta identifica a la persona que recibe.
 *
 * Mismo criterio que la línea del presupuesto (`quoteDetail.tsx`) y que la vista
 * de datos de facturación (`BillingDataModal`), que ya iban por `company`.
 */
const displayName = (a: any, type: AddressType) => {
  const full = [cleanField(a?.firstname), cleanField(a?.lastname)]
    .filter(Boolean)
    .join(" ");
  const company = cleanField(a?.company);
  return (type === "billing" ? company || full : full || company) || "—";
};

/**
 * Filter addresses from the user-address endpoint by their addressType flag.
 * The entity exposes addressType as { shipping: bool, billing: bool }.
 * Some legacy rows store it as a string ("shipping"/"billing"), so handle both.
 */
const matchesAddressType = (a: any, type: AddressType): boolean => {
  const at = a?.addressType;
  if (!at) return true;
  if (typeof at === "string") return at === type;
  if (typeof at === "object") return Boolean(at[type]);
  return true;
};

export const QuoteAddressBookPopup: React.FC<QuoteAddressBookPopupProps> = ({
  open,
  onClose,
  quoteId,
  userId,
  type,
  quoteLabel,
  storeCode,
  onSaved,
}) => {
  const [addresses, setAddresses] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editingAddress, setEditingAddress] = useState<any | null>(null);

  const filteredAddresses = useMemo(
    () => addresses.filter((a) => matchesAddressType(a, type)),
    [addresses, type],
  );

  const fetchAddresses = async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const params: Record<string, string | number> = {
        user_id: Number(userId),
        type,
      };
      const trimmedStore = String(storeCode ?? "").trim();
      if (trimmedStore) params.store = trimmedStore;
      const { data } = await axiosInstance.get(API_URL_USER_ADDRESSES_V1, {
        params,
      });
      const list = Array.isArray(data) ? data : data?.data || [];
      setAddresses(list.map(fromV1));
    } catch {
      setAddresses([]);
      message.error("Error al cargar direcciones");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    setSelectedId(null);
    setFormOpen(false);
    setEditingAddress(null);
    fetchAddresses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, userId]);

  const handleApply = async () => {
    if (!selectedId) {
      message.warning("Seleccioná una dirección");
      return;
    }
    setSaving(true);
    try {
      await axiosInstance.post(
        `${API_URL_QUOTES}/${quoteId}/addresses/save`,
        { userAddressId: selectedId, type },
      );
      message.success("Dirección aplicada");
      onSaved();
      onClose();
    } catch (err: any) {
      const apiMsg =
        err?.response?.data?.message || err?.response?.data?.error;
      message.error(
        Array.isArray(apiMsg)
          ? apiMsg.join(", ")
          : apiMsg || "Error al aplicar dirección",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleStartEdit = (a: any) => {
    setEditingAddress(a);
    setFormOpen(true);
  };

  const handleStartCreate = () => {
    setEditingAddress(null);
    setFormOpen(true);
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    setEditingAddress(null);
  };

  const handleAgendaUpdate = async (updated: any) => {
    // Persist via PATCH if it's an existing address, or POST if the user
    // is creating a brand-new one through "Agregar nueva". Form components
    // emit the legacy shape; the adapter translates to /v1/addresses.
    try {
      const legacy: Record<string, any> = {
        firstname: updated.firstname,
        lastname: updated.lastname,
        telephone: updated.telephone,
        street: updated.street,
        city: updated.city,
        region: updated.region,
        postcode: updated.postcode,
        countryId: updated.countryId,
        latitude: updated.latitude,
        longitude: updated.longitude,
        shippingIndication: updated.shippingIndication,
        company: updated.company,
        vatId: updated.vatId,
        billingTypeId: updated.billingTypeId,
      };
      Object.keys(legacy).forEach(
        (k) => legacy[k] === undefined && delete legacy[k],
      );

      if (updated?.userAddressId) {
        await axiosInstance.patch(
          `${API_URL_USER_ADDRESSES_V1}/${updated.userAddressId}`,
          toV1Update(legacy),
        );
        message.success("Dirección actualizada");
      } else {
        await axiosInstance.post(
          API_URL_USER_ADDRESSES_V1,
          toV1Create(legacy, type, userId, storeCode ?? null),
        );
        message.success("Dirección agregada a la agenda");
      }

      await fetchAddresses();
      setFormOpen(false);
      setEditingAddress(null);
    } catch (err: any) {
      const apiMsg = err?.response?.data?.message || err?.response?.data?.error;
      message.error(
        Array.isArray(apiMsg)
          ? apiMsg.join(", ")
          : apiMsg || "Error al guardar la dirección",
      );
    }
  };

  const TitleIcon = type === "shipping" ? TruckOutlined : FileTextOutlined;
  const titleMain =
    type === "shipping" ? "Agenda de entregas" : "Agenda de facturación";

  return (
    <>
      <Modal
        open={open}
        onCancel={onClose}
        centered
        width={560}
        footer={null}
        closable={false}
        destroyOnClose
        styles={{ body: { padding: 20 } }}
      >
        <Flex align="center" gap={8} style={{ marginBottom: 16 }}>
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={onClose}
            size="small"
          />
          <TitleIcon style={{ fontSize: 18, color: "#15814b" }} />
          <Title level={5} style={{ margin: 0 }}>
            {titleMain}
          </Title>
          {quoteLabel ? (
            <Text type="secondary" style={{ fontSize: 12 }}>
              · presupuesto #{quoteLabel}
            </Text>
          ) : null}
        </Flex>

        <div
          style={{
            border: "1px solid #e8e8e8",
            borderRadius: 8,
            maxHeight: 360,
            overflowY: "auto",
            background: "#fff",
          }}
        >
          {loading ? (
            <Flex justify="center" style={{ padding: 40 }}>
              <Spin />
            </Flex>
          ) : filteredAddresses.length === 0 ? (
            <Flex justify="center" style={{ padding: 24 }}>
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="El cliente aún no tiene direcciones cargadas"
              />
            </Flex>
          ) : (
            <Radio.Group
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
              style={{ width: "100%" }}
            >
              <Flex vertical>
                {filteredAddresses.map((a, idx) => {
                  const id = a.userAddressId || a.id;
                  const isSelected = selectedId === id;
                  return (
                    <Flex
                      key={id}
                      align="start"
                      gap={12}
                      style={{
                        padding: "12px 16px",
                        background: isSelected ? "#eaf5ee" : "transparent",
                        borderTop: idx === 0 ? "none" : "1px solid #f0f0f0",
                        cursor: "pointer",
                        transition: "background .15s",
                      }}
                      onClick={() => setSelectedId(id)}
                    >
                      <Radio value={id} style={{ marginTop: 2 }} />
                      <Flex vertical style={{ flex: 1, minWidth: 0 }}>
                        <Text strong style={{ fontSize: 13 }}>
                          {displayName(a, type)}
                        </Text>
                        <Flex
                          align="center"
                          gap={4}
                          style={{ marginTop: 2 }}
                        >
                          <EnvironmentOutlined
                            style={{ fontSize: 12, color: "#666" }}
                          />
                          <Text
                            type="secondary"
                            style={{ fontSize: 12 }}
                            ellipsis
                          >
                            {formatAddressLine(a)}
                            {a.telephone ? `, ${a.telephone}` : ""}
                          </Text>
                        </Flex>
                        {type === "billing" && a.vatId ? (
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            CUIT: {a.vatId}
                          </Text>
                        ) : null}
                        {type === "shipping" && a.shippingIndication ? (
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {a.shippingIndication}
                          </Text>
                        ) : null}
                      </Flex>
                      <Button
                        type="text"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleStartEdit(a);
                        }}
                      />
                    </Flex>
                  );
                })}
              </Flex>
            </Radio.Group>
          )}
        </div>

        <Flex justify="space-between" align="center" style={{ marginTop: 20 }}>
          <Button onClick={handleStartCreate}>Agregar nueva</Button>
          <Flex gap={8}>
            <Button onClick={onClose}>Cancelar</Button>
            <Button
              type="primary"
              loading={saving}
              disabled={!selectedId}
              onClick={handleApply}
              style={{
                background: selectedId ? "#1a5e2a" : undefined,
                borderColor: selectedId ? "#1a5e2a" : undefined,
              }}
            >
              Usar esta dirección
            </Button>
          </Flex>
        </Flex>
      </Modal>

      <Drawer
        open={formOpen}
        onClose={handleCloseForm}
        placement="right"
        width={520}
        destroyOnClose
        title={editingAddress ? "Editar dirección" : "Nueva dirección"}
      >
        {type === "shipping" ? (
          <DeliveryAgenda
            address={editingAddress}
            visible={formOpen}
            onClose={handleCloseForm}
            onUpdate={handleAgendaUpdate}
          />
        ) : (
          <QuoteBillingAgendaForm
            address={editingAddress}
            onClose={handleCloseForm}
            onUpdate={handleAgendaUpdate}
          />
        )}
      </Drawer>
    </>
  );
};
